import { ModalDialog } from '@ads/components-react';
import { useTranslation } from 'react-i18next';

/** Props de `UpdateParcelsConfirmModal`. */
export interface UpdateParcelsConfirmModalProps {
  /** Controla a visibilidade. */
  readonly open: boolean;
  /** Quantidade de parcelas em aberto que serão recriadas. */
  readonly openParcelsCount: number;
  /** `true` enquanto o `PUT` está em voo. */
  readonly isPending: boolean;
  /** Confirma o salvamento com recriação de parcelas. */
  readonly onConfirm: () => void;
  /** Fecha sem salvar. */
  readonly onClose: () => void;
}

/**
 * Confirmação de salvamento com recriação das parcelas em aberto.
 *
 * Disparada só quando `updatePlanParcels` está marcado. O texto declara **quantas**
 * parcelas serão recalculadas e reemitidas, porque o efeito é irreversível e o
 * operador precisa dimensioná-lo antes de confirmar — um "tem certeza?" genérico
 * não dá base para a decisão.
 *
 * Fica sempre montado e é fechado por `open → false`: desmontar um dialog Radix
 * aberto deixa `pointer-events: none` no `body` e trava a página.
 */
export function UpdateParcelsConfirmModal({
  open,
  openParcelsCount,
  isPending,
  onConfirm,
  onClose,
}: UpdateParcelsConfirmModalProps) {
  const { t } = useTranslation('individualPayment');

  return (
    <ModalDialog
      open={open}
      onOpenChange={next => {
        // Fechar por X, Escape ou overlay só vale quando não há request em voo.
        if (!next && !isPending) onClose();
      }}
      type="warning"
      title={t('planEdit.confirm.title')}
      description={t('planEdit.confirm.description', { count: openParcelsCount })}
      primaryAction={{
        label: t('planEdit.confirm.confirm'),
        onClick: onConfirm,
        isLoading: isPending,
        disabled: isPending,
      }}
      secondaryAction={{
        label: t('planEdit.confirm.cancel'),
        onClick: onClose,
        disabled: isPending,
      }}
      // Nome distinto do botão secundário: dois controles chamados "Cancelar"
      // ficam indistinguíveis para leitor de tela e para teste.
      closeAriaLabel={t('planEdit.close')}
    />
  );
}
